import { useCallback, useEffect, useState } from 'react';
import {
  GEMINI_LANGUAGE_OPTIONS,
  normalizeGeminiLanguage,
} from '../utils/openExternalAi';
import {
  GEMINI_LANGUAGE_CHANGE_EVENT,
  dispatchGeminiLanguageChange,
  isValidGeminiLanguage,
  readDefaultGeminiLanguage,
  readLegacyGeminiLanguageFallback,
  writeDefaultGeminiLanguage,
} from '../utils/geminiLanguageStorage';
import { useBoardStore } from '../store/useBoardStore';

export {
  GEMINI_LANGUAGE_CHANGE_EVENT,
  GEMINI_LANGUAGE_STORAGE_KEY,
  readDefaultGeminiLanguage,
  readLegacyGeminiLanguageFallback,
  resolveSeedGeminiLanguage,
  writeDefaultGeminiLanguage,
} from '../utils/geminiLanguageStorage';

export function useGeminiLanguage() {
  const hashtag = useBoardStore(state => state.hashtag);
  const boardLocale = useBoardStore(state => state.board?.locale);
  const saveLocale = useBoardStore(state => state.saveLocale);
  const [defaultLanguage, setDefaultLanguage] = useState(readDefaultGeminiLanguage);

  useEffect(() => {
    const syncDefaultFromStorage = () => {
      setDefaultLanguage(readDefaultGeminiLanguage());
    };
    const onCustomChange = event => {
      const next = event?.detail?.language;
      const eventHashtag = event?.detail?.hashtag;
      // Board-scoped changes update the board store; only refresh the default preference here.
      if (eventHashtag != null && eventHashtag !== '') {
        if (next && isValidGeminiLanguage(next)) {
          setDefaultLanguage(next);
        } else {
          syncDefaultFromStorage();
        }
        return;
      }
      if (next && isValidGeminiLanguage(next)) {
        setDefaultLanguage(next);
        return;
      }
      syncDefaultFromStorage();
    };

    window.addEventListener('storage', syncDefaultFromStorage);
    window.addEventListener(GEMINI_LANGUAGE_CHANGE_EVENT, onCustomChange);
    return () => {
      window.removeEventListener('storage', syncDefaultFromStorage);
      window.removeEventListener(GEMINI_LANGUAGE_CHANGE_EVENT, onCustomChange);
    };
  }, []);

  const geminiLanguage = hashtag
    ? normalizeGeminiLanguage(boardLocale || readLegacyGeminiLanguageFallback())
    : defaultLanguage;

  const setGeminiLanguage = useCallback(
    nextLanguage => {
      if (!isValidGeminiLanguage(nextLanguage)) {
        return;
      }
      const normalized = writeDefaultGeminiLanguage(nextLanguage);
      setDefaultLanguage(normalized);
      if (hashtag) {
        saveLocale(normalized);
      }
      dispatchGeminiLanguageChange({ language: normalized, hashtag: hashtag || null });
    },
    [hashtag, saveLocale]
  );

  const selectedLabel =
    GEMINI_LANGUAGE_OPTIONS.find(option => option.code === geminiLanguage)?.label || 'English';

  return { geminiLanguage, setGeminiLanguage, selectedLabel, GEMINI_LANGUAGE_OPTIONS };
}
